/**
 * ExcelExportModal — مودال خروجی اکسل با قابلیت انتخاب ستون و محدوده ردیف
 *
 * استفاده:
 *   import ExcelExportModal from '../../components/ExcelExportModal';
 *
 *   {showExcel && (
 *     <ExcelExportModal
 *       title="اعتبارات"
 *       subtitle="سال مالی ۱۴۰۵"
 *       columns={[{ key:'code', label:'کد', num:true }, { key:'name', label:'نام' }]}
 *       rows={data}
 *       onClose={() => setShowExcel(false)}
 *     />
 *   )}
 *
 * یا ساده‌تر بدون مودال:
 *   import { ExcelButton } from '../../components/ExcelExportModal';
 *   <ExcelButton title="..." columns={cols} rows={data} />
 */
import React, { useState, useMemo, useCallback } from 'react';
import { exportGrid, exportCsv } from './excel';

/* ─── کمکی ─── */
const toFa = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);

/* ─── مودال خروجی اکسل ─── */
export default function ExcelExportModal({ title, subtitle, columns, rows, onClose }) {
  const allCols = useMemo(() => (columns || []).filter(Boolean), [columns]);
  const allRows = rows || [];

  // وضعیت چک‌باکس ستون‌ها — true = انتخاب شده
  const [checked, setChecked] = useState(() => allCols.map(() => true));
  // محدوده ردیف
  const [fromRow, setFromRow] = useState(1);
  const [toRow, setToRow]     = useState(allRows.length);
  // فرمت خروجی
  const [format, setFormat]   = useState('xls'); // 'xls' | 'csv'

  // ستون‌های انتخاب‌شده
  const selectedCols = useMemo(
    () => allCols.filter((_, i) => checked[i]),
    [allCols, checked]
  );

  // ردیف‌های محدوده
  const selectedRows = useMemo(() => {
    const f = Math.max(1, fromRow) - 1;
    const t = Math.min(allRows.length, toRow);
    return allRows.slice(f, t);
  }, [allRows, fromRow, toRow]);

  const count = Math.max(0, Math.min(allRows.length, toRow) - Math.max(1, fromRow) + 1);

  // عملیات انتخاب
  const selectAll    = () => setChecked(allCols.map(() => true));
  const selectNone   = () => setChecked(allCols.map(() => false));
  const selectToggle = () => setChecked(prev => prev.map(v => !v));
  const toggle = useCallback((idx) => {
    setChecked(prev => { const n = [...prev]; n[idx] = !n[idx]; return n; });
  }, []);

  // خروجی
  const doExport = () => {
    if (selectedCols.length === 0) return;
    const params = { title, subtitle, columns: selectedCols, rows: selectedRows };
    if (format === 'csv') exportCsv(params);
    else                  exportGrid(params);
    if (onClose) onClose();
  };

  const canExport = selectedCols.length > 0 && count > 0;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* هدر */}
        <div style={header}>
          <div style={headerTitle}>📊 خروجی اکسل</div>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={body}>
          {/* عنوان گزارش */}
          <div style={section}>
            <div style={sLabel}>عنوان گزارش:</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a2f50' }}>
              {title || 'گزارش'}
              {subtitle && <span style={{ fontWeight: '400', color: '#666', marginRight: '8px', fontSize: '12px' }}> — {subtitle}</span>}
            </div>
          </div>

          {/* انتخاب ستون‌ها */}
          <div style={section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={sLabel}>ستون‌ها ({toFa(selectedCols.length)} از {toFa(allCols.length)}):</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="button" onClick={selectAll} style={chipBtn}>همه</button>
                <button type="button" onClick={selectNone} style={chipBtn}>هیچ‌کدام</button>
                <button type="button" onClick={selectToggle} style={chipBtn}>معکوس</button>
              </div>
            </div>
            <div style={colList}>
              {allCols.map((c, i) => (
                <label key={c.key || i} style={colItem}>
                  <input
                    type="checkbox"
                    checked={checked[i]}
                    onChange={() => toggle(i)}
                    style={{ accentColor: '#1d6f42' }}
                  />
                  <span style={{ fontSize: '12px', color: checked[i] ? '#1a2f50' : '#aaa' }}>
                    {c.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* محدوده ردیف */}
          <div style={section}>
            <div style={sLabel}>محدوده ردیف‌ها:</div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '6px' }}>
              <label style={rangeLbl}>
                از ردیف:
                <input type="number" min={1} max={allRows.length}
                  value={fromRow} onChange={e => setFromRow(Number(e.target.value) || 1)}
                  style={rangeInput} dir="ltr" />
              </label>
              <label style={rangeLbl}>
                تا ردیف:
                <input type="number" min={1} max={allRows.length}
                  value={toRow} onChange={e => setToRow(Number(e.target.value) || allRows.length)}
                  style={rangeInput} dir="ltr" />
              </label>
              <div style={{ fontSize: '12px', color: '#888', marginRight: 'auto' }}>
                {toFa(count)} ردیف از {toFa(allRows.length)}
              </div>
            </div>
          </div>

          {/* فرمت خروجی */}
          <div style={section}>
            <div style={sLabel}>فرمت:</div>
            <div style={{ display: 'flex', gap: '14px', marginTop: '6px' }}>
              <label style={radioLbl}>
                <input type="radio" checked={format === 'xls'} onChange={() => setFormat('xls')} />
                اکسل (.xls)
              </label>
              <label style={radioLbl}>
                <input type="radio" checked={format === 'csv'} onChange={() => setFormat('csv')} />
                CSV
              </label>
            </div>
          </div>

          {/* پیش‌نمایش ستون‌های انتخاب‌شده */}
          {selectedCols.length > 0 && (
            <div style={section}>
              <div style={sLabel}>پیش‌نمایش:</div>
              <div style={previewWrap}>
                <table style={previewTable}>
                  <thead>
                    <tr>
                      <th style={pvTh}>ردیف</th>
                      {selectedCols.map(c => <th key={c.key} style={pvTh}>{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRows.slice(0, 3).map((r, i) => (
                      <tr key={i}>
                        <td style={pvTd}>{toFa(i + Math.max(1, fromRow))}</td>
                        {selectedCols.map(c => (
                          <td key={c.key} style={{ ...pvTd, direction: c.num ? 'ltr' : 'rtl' }}>
                            {c.num && r[c.key] != null ? Number(r[c.key]).toLocaleString('fa-IR') : (r[c.key] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {count > 3 && (
                      <tr><td colSpan={selectedCols.length + 1} style={{ ...pvTd, textAlign: 'center', color: '#aaa' }}>… و {toFa(count - 3)} ردیف دیگر</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* فوتر */}
        <div style={footer}>
          <button onClick={onClose} style={btnCancel}>انصراف</button>
          <button onClick={doExport} disabled={!canExport}
            style={{ ...btnExport, opacity: canExport ? 1 : 0.4 }}>
            {format === 'csv' ? '⬇ دانلود CSV' : '⬇ دانلود اکسل'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * دکمهٔ ساده — بدون مودال، مستقیم اکسپورت می‌کند.
 * (همان ExcelButton قبلی؛ اینجا re-export می‌شود برای سازگاری)
 */
export { ExcelButton } from './excel';

/**
 * دکمه‌ای که مودال انتخاب ستون را باز می‌کند.
 */
export function ExcelModalButton({ title, subtitle, columns, rows, style, label = '📊 خروجی اکسل' }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        title={`خروجی اکسل — ${title || ''}`}
        style={{
          padding: '5px 10px', background: '#1d6f42', color: '#fff', border: 'none',
          borderRadius: '6px', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700,
          cursor: 'pointer', whiteSpace: 'nowrap', ...(style || {}),
        }}>
        {label}
      </button>
      {open && (
        <ExcelExportModal
          title={title} subtitle={subtitle} columns={columns} rows={rows}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/* ─── استایل‌ها ─── */
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999,
};
const modal = {
  background: '#fff', borderRadius: '14px', width: '95%', maxWidth: '620px',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
};
const header = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '14px 20px', background: 'linear-gradient(135deg,#1d6f42,#2e7d32)',
  color: '#fff',
};
const headerTitle = { fontSize: '15px', fontWeight: '700' };
const closeBtn = {
  background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
  width: '28px', height: '28px', borderRadius: '50%', fontSize: '14px',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body = { flex: 1, overflowY: 'auto', padding: '16px 20px' };
const section = { marginBottom: '16px' };
const sLabel = { fontSize: '12px', fontWeight: '700', color: '#555', marginBottom: '4px' };
const colList = {
  display: 'flex', flexWrap: 'wrap', gap: '6px',
  maxHeight: '160px', overflowY: 'auto',
  border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px',
  background: '#fafafa',
};
const colItem = {
  display: 'flex', alignItems: 'center', gap: '4px',
  padding: '3px 8px', borderRadius: '6px', cursor: 'pointer',
  background: '#fff', border: '1px solid #eee',
};
const chipBtn = {
  padding: '3px 10px', fontSize: '11px', border: '1px solid #ccc',
  borderRadius: '12px', background: '#f5f5f5', cursor: 'pointer',
  color: '#555', fontFamily: 'inherit',
};
const rangeLbl = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555' };
const rangeInput = {
  width: '70px', padding: '4px 8px', border: '1px solid #ccc',
  borderRadius: '6px', fontSize: '13px', textAlign: 'center', fontFamily: 'inherit',
};
const radioLbl = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#333', cursor: 'pointer' };
const previewWrap = {
  border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'auto',
  maxHeight: '140px', marginTop: '6px',
};
const previewTable = { width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: "'Vazirmatn',Tahoma,sans-serif" };
const pvTh = {
  padding: '5px 8px', background: '#1d6f42', color: '#fff',
  fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap',
  borderBottom: '2px solid #145a30', position: 'sticky', top: 0,
};
const pvTd = { padding: '4px 8px', borderBottom: '1px solid #eee', textAlign: 'center' };
const footer = {
  display: 'flex', justifyContent: 'flex-end', gap: '10px',
  padding: '12px 20px', borderTop: '1px solid #eee', background: '#fafafa',
};
const btnCancel = {
  padding: '8px 18px', border: '1px solid #ccc', borderRadius: '8px',
  background: '#fff', color: '#555', fontSize: '13px', fontWeight: '600',
  cursor: 'pointer', fontFamily: 'inherit',
};
const btnExport = {
  padding: '8px 22px', border: 'none', borderRadius: '8px',
  background: '#1d6f42', color: '#fff', fontSize: '13px', fontWeight: '700',
  cursor: 'pointer', fontFamily: 'inherit',
};
